import {
	CodeBlock,
	Alert,
	Step,
	Table,
	Card,
	CardGrid,
	Diagram,
} from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function IntegrationGuide() {
	const walletModeColumns = [
		{ key: 'feature', header: 'Feature' },
		{ key: 'integrated', header: 'Integrated Mode' },
		{ key: 'seamless', header: 'Seamless Mode' },
	];

	const walletModeData = [
		{
			feature: 'Balance Management',
			integrated: 'Ringfort manages player balances',
			seamless: 'Your system manages balances',
		},
		{
			feature: 'Deposit/Withdraw',
			integrated: 'Built-in deposit and withdrawal UI',
			seamless: 'Handled by your platform',
		},
		{
			feature: 'Integration Effort',
			integrated: 'Minimal - just embed games',
			seamless: 'Requires wallet callback endpoints',
		},
		{
			feature: 'Real-time Balance',
			integrated: 'Automatic synchronization',
			seamless: 'Via callback requests',
		},
		{
			feature: 'Best For',
			integrated: 'New platforms, quick launch',
			seamless: 'Existing wallet systems',
		},
	];

	const checklistColumns = [
		{ key: 'item', header: 'Checklist Item' },
		{ key: 'description', header: 'Description' },
	];

	const checklistData = [
		{
			item: 'API Key Security',
			description:
				'Store secret keys server-side only. Never expose sk_live_* keys to clients.',
		},
		{
			item: 'Webhook Verification',
			description:
				'Verify all webhook signatures using HMAC-SHA256 before processing.',
		},
		{
			item: 'Error Handling',
			description:
				'Implement retry logic with exponential backoff for failed requests.',
		},
		{
			item: 'Session Management',
			description:
				'Handle session expiration gracefully and prompt players to re-authenticate.',
		},
		{
			item: 'Testing Complete',
			description:
				'Test all game flows in sandbox environment before going live.',
		},
		{
			item: 'Monitoring',
			description:
				'Set up alerts for webhook failures and unusual betting patterns.',
		},
		{
			item: 'Rate Limiting',
			description:
				'Implement client-side rate limiting to avoid hitting API limits.',
		},
		{
			item: 'Data Validation',
			description:
				'Validate all input data and handle edge cases in bet amounts.',
		},
	];

	return (
		<div className={styles.docsPage}>
			<h1>Integration Guide</h1>
			<p className={styles.lead}>
				Learn how to integrate Ringfort Studio games into your platform. This
				guide covers the complete integration process from architecture overview
				to production deployment.
			</p>

			<h2>Architecture Overview</h2>
			<p>
				Ringfort Studio uses a B2B integration model where your backend
				communicates with our API, and players interact with our game client
				through authenticated URLs.
			</p>

			<Diagram
				title="Integration Flow"
				steps={[
					{
						label: 'Your Backend',
						description: 'Create session via POST /sessions',
						highlight: true,
					},
					{
						label: 'Ringfort API',
						description: 'Returns game URL + session token',
					},
					{
						label: 'Game Client',
						description: 'Player redirected with token',
					},
					{
						label: 'Player Gameplay',
						description: 'Bets processed by Ringfort',
						highlight: true,
					},
					{
						label: 'Wallet Callbacks',
						description: 'Real-time balance updates (seamless mode)',
					},
					{
						label: 'Webhooks',
						description: 'Event notifications to your backend',
					},
				]}
			/>

			<h3>Key Components</h3>
			<ul>
				<li>
					<strong>Your Backend:</strong> Handles player authentication, session
					creation, and optionally wallet callbacks
				</li>
				<li>
					<strong>Ringfort API:</strong> Processes game logic, manages sessions,
					and sends webhooks
				</li>
				<li>
					<strong>Game Client:</strong> Hosted by Ringfort, embedded in your
					platform via iframe or redirect
				</li>
			</ul>

			<h2>Step-by-Step Integration</h2>

			<Step number={1} title="Get Your API Credentials">
				<p>
					Sign up for a Ringfort Studio account and obtain your API keys from
					the operator dashboard. You will receive two key pairs - one for
					sandbox testing and one for production.
				</p>
				<CodeBlock language="bash" title="API Key Format">
					{`# Sandbox (Testing)
Public Key: pk_test_xxxxxxxxxxxxxxxx
Secret Key: sk_test_xxxxxxxxxxxxxxxx

# Production (Live)
Public Key: pk_live_xxxxxxxxxxxxxxxx
Secret Key: sk_live_xxxxxxxxxxxxxxxx`}
				</CodeBlock>
				<Alert variant="warning" title="Keep Your Secret Key Safe">
					Never expose your secret key in client-side code. Store it securely in
					environment variables and only use it in server-side requests.
				</Alert>
			</Step>

			<Step number={2} title="Set Up Your Backend Endpoints">
				<p>
					Create the necessary endpoints in your backend to handle session
					creation and webhook processing.
				</p>
				<CodeBlock language="typescript" title="Backend Setup (Node.js/Express)">
					{`import express from 'express';
import crypto from 'crypto';

const app = express();
const RINGFORT_SECRET = process.env.RINGFORT_SECRET_KEY;
const RINGFORT_API = 'https://api.rngfort.com';

// Create a game session for a player
app.post('/api/launch-game', async (req, res) => {
  const { playerId, gameId } = req.body;

  // Verify player is authenticated with your system
  const player = await getAuthenticatedPlayer(req);
  if (!player) {
    return res.status(401).json({ error: 'Unauthorized' });
  }

  // Create session with Ringfort
  const response = await fetch(\`\${RINGFORT_API}/api/v1/operators/sessions\`, {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${RINGFORT_SECRET}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      playerId: player.id,
      currency: 'USD',
      walletMode: 'seamless', // or 'integrated'
      returnUrl: 'https://yoursite.com/games',
    }),
  });

  const data = await response.json();

  if (data.success) {
    res.json({
      gameUrl: data.data.gameUrl,
      sessionId: data.data.sessionId,
    });
  } else {
    res.status(400).json({ error: data.error });
  }
});`}
				</CodeBlock>
			</Step>

			<Step number={3} title="Embed the Game Client">
				<p>
					Once you have a game URL, you can either redirect the player or embed
					the game in an iframe.
				</p>
				<CodeBlock language="typescript" title="Frontend Integration (React)">
					{`import { useState } from 'react';

function GameLauncher({ gameId }: { gameId: string }) {
  const [gameUrl, setGameUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const launchGame = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/launch-game', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gameId }),
      });

      const data = await response.json();
      if (data.gameUrl) {
        setGameUrl(data.gameUrl);
      }
    } catch (error) {
      console.error('Failed to launch game:', error);
    } finally {
      setLoading(false);
    }
  };

  if (gameUrl) {
    return (
      <iframe
        src={gameUrl}
        width="100%"
        height="600"
        style={{ border: 'none', borderRadius: '8px' }}
        allow="fullscreen"
      />
    );
  }

  return (
    <button onClick={launchGame} disabled={loading}>
      {loading ? 'Loading...' : 'Play Now'}
    </button>
  );
}`}
				</CodeBlock>
			</Step>

			<Step number={4} title="Configure Wallet Mode">
				<p>
					Choose between integrated and seamless wallet modes based on your
					platform's requirements.
				</p>
				<Table columns={walletModeColumns} data={walletModeData} />
				<p>
					For seamless mode, you will need to implement wallet callback endpoints.
					See the <a href="/docs/wallet-modes">Wallet Modes documentation</a>{' '}
					for detailed implementation guides.
				</p>
			</Step>

			<Step number={5} title="Implement Webhook Handlers">
				<p>
					Set up webhook endpoints to receive real-time notifications about game
					events like bets placed and settled.
				</p>
				<CodeBlock language="typescript" title="Webhook Handler">
					{`import crypto from 'crypto';

// Verify webhook signature
function verifyWebhookSignature(
  payload: string,
  signature: string,
  secret: string
): boolean {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(payload)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expected)
  );
}

app.post('/webhooks/ringfort', express.raw({ type: 'application/json' }), (req, res) => {
  const signature = req.headers['x-signature'] as string;
  const payload = req.body.toString();

  if (!verifyWebhookSignature(payload, signature, RINGFORT_SECRET)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }

  const event = JSON.parse(payload);

  switch (event.type) {
    case 'bet.placed':
      console.log('Bet placed:', event.data.betId);
      // Update your records
      break;
    case 'bet.settled':
      console.log('Bet settled:', event.data.betId, 'Won:', event.data.won);
      // Update player statistics
      break;
    case 'session.expired':
      console.log('Session expired:', event.data.sessionId);
      // Clean up any session state
      break;
  }

  res.json({ received: true });
});`}
				</CodeBlock>
			</Step>

			<Step number={6} title="Test and Go Live">
				<p>
					Thoroughly test your integration in sandbox mode before switching to
					production.
				</p>
				<ol>
					<li>
						Test all game flows with sandbox API keys (pk_test_*, sk_test_*)
					</li>
					<li>Verify webhook delivery and signature verification</li>
					<li>Test wallet callbacks if using seamless mode</li>
					<li>Test error handling and edge cases</li>
					<li>Switch to production API keys (pk_live_*, sk_live_*)</li>
					<li>Monitor initial production traffic closely</li>
				</ol>
			</Step>

			<h2>Common Integration Patterns</h2>

			<h3>Casino Platform</h3>
			<p>
				For traditional casino platforms with existing player accounts and wallet
				systems, seamless mode provides the best integration.
			</p>
			<CodeBlock language="typescript" title="Casino Integration">
				{`// Casino typically uses seamless mode to maintain single wallet
const createCasinoSession = async (playerId: string) => {
  const response = await fetch(\`\${RINGFORT_API}/api/v1/operators/sessions\`, {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${RINGFORT_SECRET}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      playerId,
      currency: 'USD',
      walletMode: 'seamless',
      // Callbacks configured in operator dashboard
      returnUrl: 'https://casino.com/lobby',
    }),
  });

  return response.json();
};

// Wallet callback endpoint for casino
app.post('/wallet/debit', async (req, res) => {
  const { playerId, amount, transactionId, betId } = req.body;

  // Deduct from player's casino wallet
  const result = await casinoWallet.debit(playerId, amount, {
    reference: betId,
    type: 'game_bet',
  });

  res.json({
    transactionId: result.transactionId,
    balance: result.newBalance,
  });
});`}
			</CodeBlock>

			<h3>Crypto Gambling Site</h3>
			<p>
				Crypto platforms can use integrated mode for simpler setup or seamless
				mode for multi-currency support.
			</p>
			<CodeBlock language="typescript" title="Crypto Platform Integration">
				{`// Support multiple cryptocurrencies
const SUPPORTED_CURRENCIES = ['BTC', 'ETH', 'USDT', 'USDC'];

const createCryptoSession = async (
  playerId: string,
  currency: string
) => {
  if (!SUPPORTED_CURRENCIES.includes(currency)) {
    throw new Error('Unsupported currency');
  }

  const response = await fetch(\`\${RINGFORT_API}/api/v1/operators/sessions\`, {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${RINGFORT_SECRET}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      playerId,
      currency, // BTC, ETH, etc.
      walletMode: 'seamless',
      returnUrl: 'https://cryptosite.com/games',
    }),
  });

  return response.json();
};

// Handle crypto-specific precision
app.post('/wallet/credit', async (req, res) => {
  const { playerId, amount, currency } = req.body;

  // Amount is in smallest unit (satoshis for BTC)
  const result = await cryptoWallet.credit(playerId, amount, currency);

  res.json({
    transactionId: result.txId,
    balance: result.balance, // In smallest unit
  });
});`}
			</CodeBlock>

			<h3>Gaming Platform</h3>
			<p>
				Gaming platforms with virtual currencies can use integrated mode for a
				quick implementation.
			</p>
			<CodeBlock language="typescript" title="Gaming Platform Integration">
				{`// Gaming platform with virtual currency
const createGamingSession = async (playerId: string) => {
  const response = await fetch(\`\${RINGFORT_API}/api/v1/operators/sessions\`, {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${RINGFORT_SECRET}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      playerId,
      currency: 'COINS', // Platform's virtual currency
      walletMode: 'integrated',
      returnUrl: 'https://gamingplatform.com/arcade',
    }),
  });

  return response.json();
};

// Deposit virtual currency to Ringfort
const depositToRingfort = async (playerId: string, amount: number) => {
  // First deduct from your platform
  await platformWallet.deduct(playerId, amount);

  // Then deposit to Ringfort
  const response = await fetch(
    \`\${RINGFORT_API}/api/v1/operators/players/\${playerId}/deposit\`,
    {
      method: 'POST',
      headers: {
        'Authorization': \`Bearer \${RINGFORT_SECRET}\`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: String(amount),
        currency: 'COINS',
      }),
    }
  );

  return response.json();
};`}
			</CodeBlock>

			<h2>Production Checklist</h2>
			<p>
				Before going live, ensure you have completed all items in this checklist.
			</p>
			<Table columns={checklistColumns} data={checklistData} />

			<Alert variant="success" title="Ready to Launch?">
				Once you have completed the checklist, switch your API keys from sandbox
				to production and you are ready to accept real bets. Start with lower
				limits and gradually increase as you verify everything works correctly.
			</Alert>

			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>
				<CardGrid columns={3}>
					<Card
						title="Player Sessions"
						description="Learn about session management and authentication"
						href="/docs/sessions"
					/>
					<Card
						title="Wallet Modes"
						description="Deep dive into integrated vs seamless modes"
						href="/docs/wallet-modes"
					/>
					<Card
						title="Webhooks"
						description="Handle real-time game events"
						href="/docs/webhooks"
					/>
				</CardGrid>
			</div>
		</div>
	);
}
